// src/pages/team/TravelPage.jsx
// ============================================================================
// Travel log: where someone was, by date range and country.
//
// Admins and admin-pa only (App.js adminOnlyPages, RLS on travel_log): they
// see and edit everyone's trips and pick the person on the form. The VOF hours statement reads country IN rows: a
// partner's hours on those days are SBPL's own work. Time entries are never
// touched from here.
// ============================================================================

import React, { useMemo, useState } from 'react';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import {
  BrioPageHeader,
  BrioFrame,
  BrioTableWrapper,
  BrioTable,
  BrioButton,
  BrioModal,
  BrioModalBody,
  BrioModalField,
  BrioInput,
  BrioSelect,
  BrioInfoBanner,
  BrioEmptyState,
} from '../../components/BrioComponents';
import useTravelLog from '../../hooks/useTravelLog';
import { findTravelOverlaps, formatDay, travelDays } from '../../utils/vofStatement';

// The countries people travel between most; any other ISO code can be typed.
const COMMON_COUNTRIES = [
  ['IN', 'India'],
  ['NL', 'Netherlands'],
  ['BE', 'Belgium'],
  ['DE', 'Germany'],
  ['FR', 'France'],
  ['GB', 'United Kingdom'],
  ['NO', 'Norway'],
  ['AE', 'United Arab Emirates'],
];
const countryName = (code) => (COMMON_COUNTRIES.find(c => c[0] === code) || [])[1] || code;

const emptyForm = (userId) => ({ id: null, userId: userId ?? '', startDate: '', endDate: '', country: 'IN', note: '' });

export default function TravelPage({ currentUser, isAdmin, activeUsers = [] }) {
  const travel = useTravelLog({ role: currentUser?.role });
  const [filterUser, setFilterUser] = useState(isAdmin ? 'all' : String(currentUser?.id ?? ''));
  const [form, setForm] = useState(null);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const userName = useMemo(() => {
    const m = new Map((activeUsers || []).map(u => [String(u.id), u.name]));
    return (id) => m.get(String(id)) || (String(id) === String(currentUser?.id) ? currentUser?.name : `User ${id}`);
  }, [activeUsers, currentUser]);

  const people = useMemo(() => (
    [...(activeUsers || [])]
      .filter(u => !u.deletedAt && u.status !== 'inactive')
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  ), [activeUsers]);

  const rows = useMemo(() => (
    travel.data.filter(r => filterUser === 'all' || String(r.userId) === String(filterUser))
  ), [travel.data, filterUser]);

  const overlaps = useMemo(() => (form ? findTravelOverlaps(travel.data, form) : []), [form, travel.data]);

  const openNew = () => {
    setFormError('');
    setForm(emptyForm(isAdmin && filterUser !== 'all' ? filterUser : currentUser?.id));
  };
  const openEdit = (r) => {
    setFormError('');
    setForm({ id: r.id, userId: r.userId, startDate: r.startDate, endDate: r.endDate, country: r.country, note: r.note });
  };

  const save = async () => {
    const country = String(form.country || '').trim().toUpperCase();
    if (!form.userId) return setFormError('Choose a person.');
    if (!form.startDate || !form.endDate) return setFormError('Enter both dates.');
    if (form.endDate < form.startDate) return setFormError('The end date is before the start date.');
    if (!/^[A-Z]{2}$/.test(country)) return setFormError('Country must be a two letter ISO code, for example IN or NL.');
    if (overlaps.length > 0) {
      const list = overlaps.map(o => `${countryName(o.country)}: ${formatDay(o.startDate)} to ${formatDay(o.endDate)}`).join('\n');
      // eslint-disable-next-line no-alert
      if (!window.confirm(`This overlaps with existing travel for ${userName(form.userId)}:\n\n${list}\n\nSave anyway?`)) return;
    }
    setSaving(true);
    const payload = { ...form, country, userId: isAdmin ? form.userId : currentUser?.id };
    const res = form.id ? await travel.update(form.id, payload) : await travel.create(payload);
    setSaving(false);
    if (!res.success) return setFormError(res.error);
    setForm(null);
  };

  const remove = async (r) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete travel to ${countryName(r.country)}, ${formatDay(r.startDate)} to ${formatDay(r.endDate)}?`)) return;
    const res = await travel.delete(r.id);
    // eslint-disable-next-line no-alert
    if (!res.success) window.alert(res.error);
  };

  return (
    <>
      <BrioPageHeader
        title="Travel"
        subtitle={isAdmin
          ? 'Where people were, by date. Partner days in India count as SBPL work on the VOF statement.'
          : 'Record where you were, by date.'}
        right={<BrioButton onClick={openNew}><Plus size={14} style={{ marginRight: 4, verticalAlign: '-2px' }} />Add travel</BrioButton>}
      />
      <BrioFrame>
        {isAdmin && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '16px 0' }}>
            <span className="brio-analytics-muted" style={{ fontSize: 12 }}>Person</span>
            <BrioSelect value={filterUser} onChange={e => setFilterUser(e.target.value)} style={{ maxWidth: 260 }}>
              <option value="all">Everyone</option>
              {people.map(u => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
            </BrioSelect>
          </div>
        )}

        {travel.error && <BrioInfoBanner>{travel.error}</BrioInfoBanner>}

        {travel.loading && rows.length === 0 ? (
          <BrioEmptyState>Loading travel…</BrioEmptyState>
        ) : rows.length === 0 ? (
          <BrioEmptyState>No travel recorded.</BrioEmptyState>
        ) : (
          <BrioTableWrapper>
            <BrioTable>
              <thead>
                <tr>
                  {isAdmin && <th>Person</th>}
                  <th>Country</th>
                  <th>From</th>
                  <th>To</th>
                  <th style={{ textAlign: 'right' }}>Days</th>
                  <th>Note</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const clash = findTravelOverlaps(travel.data, r).length > 0;
                  return (
                    <tr key={r.id}>
                      {isAdmin && <td>{userName(r.userId)}</td>}
                      <td>{countryName(r.country)} <span className="brio-analytics-muted">({r.country})</span></td>
                      <td>{formatDay(r.startDate)}</td>
                      <td>{formatDay(r.endDate)}</td>
                      <td style={{ textAlign: 'right' }}>{travelDays(r.startDate, r.endDate)}</td>
                      <td>
                        {r.note}
                        {clash && <span style={{ color: 'var(--brio-red, #b91c1c)', marginLeft: r.note ? 6 : 0 }} title="Overlaps another trip for this person">overlaps</span>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <button type="button" className="brio-icon-btn" onClick={() => openEdit(r)} aria-label="Edit" style={{ background: 'none', border: 0, cursor: 'pointer', padding: 4 }}><Edit2 size={14} /></button>
                        <button type="button" className="brio-icon-btn" onClick={() => remove(r)} aria-label="Delete" style={{ background: 'none', border: 0, cursor: 'pointer', padding: 4 }}><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </BrioTable>
          </BrioTableWrapper>
        )}
      </BrioFrame>

      {form && (
        <BrioModal
          title={form.id ? 'Edit travel' : 'Add travel'}
          onClose={() => setForm(null)}
          footer={(
            <>
              <BrioButton variant="secondary" onClick={() => setForm(null)}>Cancel</BrioButton>
              <BrioButton onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</BrioButton>
            </>
          )}
        >
          <BrioModalBody form>
            {isAdmin ? (
              <BrioModalField label="Person">
                <BrioSelect value={String(form.userId)} onChange={e => setForm({ ...form, userId: e.target.value })}>
                  <option value="">Choose…</option>
                  {people.map(u => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
                </BrioSelect>
              </BrioModalField>
            ) : null}
            <BrioModalField label="From">
              <BrioInput type="date" value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} />
            </BrioModalField>
            <BrioModalField label="To" hint="Inclusive: the last day there.">
              <BrioInput type="date" value={form.endDate} min={form.startDate || undefined} onChange={e => setForm({ ...form, endDate: e.target.value })} />
            </BrioModalField>
            <BrioModalField label="Country" hint="Two letter ISO code. Pick one or type another.">
              <BrioInput
                list="travel-countries"
                maxLength={2}
                value={form.country}
                onChange={e => setForm({ ...form, country: e.target.value.toUpperCase() })}
              />
              <datalist id="travel-countries">
                {COMMON_COUNTRIES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
              </datalist>
            </BrioModalField>
            <BrioModalField label="Note">
              <BrioInput value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
            </BrioModalField>
            {overlaps.length > 0 && (
              <BrioInfoBanner>
                Overlaps with {overlaps.map(o => `${countryName(o.country)} ${formatDay(o.startDate)} to ${formatDay(o.endDate)}`).join('; ')}.
              </BrioInfoBanner>
            )}
            {formError && <div style={{ color: 'var(--brio-red, #b91c1c)', fontSize: 12 }}>{formError}</div>}
          </BrioModalBody>
        </BrioModal>
      )}
    </>
  );
}
